import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { User } from "@titancloud/shared";
import { updateUserStatus, recalculateUsage } from "../../api/admin.api";
import { useAuthStore } from "../../store/authStore";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { formatBytes } from "../../utils/formatBytes";
import { QuotaEditor } from "./QuotaEditor";
import { useState } from "react";

interface UserRowProps {
  user: User;
  onUpdated: () => void;
}

const STATUS_BADGE: Record<string, "green" | "red"> = {
  APPROVED: "green",
  REVOKED: "red",
};

export function UserRow({ user, onUpdated }: UserRowProps) {
  const [editingQuota, setEditingQuota] = useState(false);
  const selfId = useAuthStore((s) => s.user?.id);

  const approveMutation = useMutation({
    mutationFn: () => updateUserStatus(user.id, "APPROVED"),
    onSuccess: onUpdated,
  });

  const revokeMutation = useMutation({
    mutationFn: () => updateUserStatus(user.id, "REVOKED"),
    onSuccess: onUpdated,
  });

  const recalcMutation = useMutation({
    mutationFn: () => recalculateUsage(user.id),
    onSuccess: onUpdated,
  });

  const usedBytes = BigInt(user.usedBytes);
  const quotaBytes = BigInt(user.quotaBytes);
  const percent = quotaBytes > 0n ? Number((usedBytes * 100n) / quotaBytes) : 0;
  const isOverQuota = percent > 100;

  return (
    <tr className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-700 text-xs">
              {user.displayName[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-medium text-gray-200">{user.displayName}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
        </div>
      </td>

      <td className="px-4 py-4">
        <Badge variant={STATUS_BADGE[user.status] ?? "gray"}>
          {user.status}
        </Badge>
        {user.role === "ADMIN" && (
          <Badge variant="blue" >ADMIN</Badge>
        )}
      </td>

      <td className="px-4 py-4">
        <div className="min-w-32">
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-300">
              {formatBytes(user.usedBytes)} / {formatBytes(user.quotaBytes)}
            </p>
            {isOverQuota && (
              <span className="rounded bg-red-900/40 px-1.5 py-0.5 text-[10px] font-medium text-red-400">
                {percent}%
              </span>
            )}
          </div>
          <div className="mt-1 h-1.5 w-full rounded-full bg-gray-700">
            <div
              className={`h-full rounded-full ${
                percent >= 90 ? "bg-red-500" : percent >= 75 ? "bg-yellow-500" : "bg-brand-500"
              }`}
              style={{ width: `${Math.min(percent, 100)}%` }}
            />
          </div>
          {isOverQuota && (
            <button
              onClick={() => recalcMutation.mutate()}
              disabled={recalcMutation.isPending}
              className="mt-1 text-[10px] text-yellow-400 hover:text-yellow-300 underline-offset-2 hover:underline disabled:opacity-50"
            >
              {recalcMutation.isPending ? "Recalculating..." : "Recalculate usage"}
            </button>
          )}
        </div>
      </td>

      <td className="px-4 py-4">
        {editingQuota ? (
          <QuotaEditor
            userId={user.id}
            currentQuotaBytes={user.quotaBytes}
            usedBytes={user.usedBytes}
            onSaved={() => {
              setEditingQuota(false);
              onUpdated();
            }}
            onCancel={() => setEditingQuota(false)}
          />
        ) : (
          <button
            onClick={() => setEditingQuota(true)}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-gray-300 hover:bg-gray-700 hover:text-brand-400 transition-colors group"
            title="Click to edit quota"
          >
            {formatBytes(user.quotaBytes)}
            <svg className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" viewBox="0 0 16 16" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
          </button>
        )}
      </td>

      <td className="px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          {selfId !== user.id && (
            <Link
              to={`/app/admin/users/${user.id}/files`}
              className="rounded-md border border-gray-600 bg-gray-800/80 px-2.5 py-1.5 text-xs font-medium text-amber-400/95 hover:border-amber-700/50 hover:bg-gray-800 hover:text-amber-300"
            >
              View files
            </Link>
          )}
          {user.status === "APPROVED" && user.role !== "ADMIN" && (
            <Button
              variant="danger"
              size="sm"
              loading={revokeMutation.isPending}
              onClick={() => revokeMutation.mutate()}
            >
              Revoke
            </Button>
          )}
          {user.status === "REVOKED" && (
            <Button
              variant="secondary"
              size="sm"
              loading={approveMutation.isPending}
              onClick={() => approveMutation.mutate()}
            >
              Restore
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
